import { useEffect, useState, type Dispatch, type SetStateAction } from 'react';
import { trackToolUsage, trackUsageAction } from '../lib/usageAnalytics';

export interface ToolPreset<T> { id: string; name: string; value: T; createdAt: string }
export interface ProcessingHistoryItem { id: string; toolId: string; summary: string; createdAt: string }
export interface WorkflowResumeItem { workflowId: string; openedAt: string }

const statePrefix = 'wyan.toolbox.state.v1.';
const presetPrefix = 'wyan.toolbox.presets.v1.';
const historyKey = 'wyan.toolbox.history.v1';
const workflowResumeKey = 'wyan.toolbox.workflow-resume.v1';

export function usePersistentToolState<T>(toolId: string, defaults: T): [T, Dispatch<SetStateAction<T>>] {
  const [value, setValue] = useState<T>(() => readJSON(`${statePrefix}${toolId}`, defaults));
  useEffect(() => { writeJSON(`${statePrefix}${toolId}`, value); }, [toolId, value]);
  return [value, setValue];
}

export function loadPresets<T>(toolId: string): ToolPreset<T>[] {
  const value = readJSON<unknown>(`${presetPrefix}${toolId}`, []);
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is ToolPreset<T> => Boolean(item && typeof item === 'object' && typeof (item as ToolPreset<T>).id === 'string' && typeof (item as ToolPreset<T>).name === 'string')).slice(0, 20);
}

export function savePreset<T>(toolId: string, name: string, value: T): ToolPreset<T>[] {
  const cleanName = name.trim().slice(0, 30);
  if (!cleanName) return loadPresets<T>(toolId);
  const current = loadPresets<T>(toolId).filter((item) => item.name !== cleanName);
  const next = [{ id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, name: cleanName, value, createdAt: new Date().toISOString() }, ...current].slice(0, 20);
  writeJSON(`${presetPrefix}${toolId}`, next);
  return next;
}

export function deletePreset<T>(toolId: string, id: string): ToolPreset<T>[] {
  const next = loadPresets<T>(toolId).filter((item) => item.id !== id);
  writeJSON(`${presetPrefix}${toolId}`, next);
  return next;
}

export function recordToolRun(toolId: string, summary: string): ProcessingHistoryItem[] {
  const next = [{ id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, toolId, summary: summary.trim().slice(0, 100), createdAt: new Date().toISOString() }, ...loadProcessingHistory()].slice(0, 50);
  writeJSON(historyKey, next);
  if (!trackToolUsage('run.success')) trackUsageAction('run.success');
  return next;
}

export function loadProcessingHistory(toolId?: string): ProcessingHistoryItem[] {
  const value = readJSON<unknown>(historyKey, []);
  if (!Array.isArray(value)) return [];
  const items = value.filter((item): item is ProcessingHistoryItem => Boolean(item && typeof item === 'object' && typeof (item as ProcessingHistoryItem).toolId === 'string' && typeof (item as ProcessingHistoryItem).summary === 'string' && typeof (item as ProcessingHistoryItem).createdAt === 'string'));
  return toolId ? items.filter((item) => item.toolId === toolId) : items;
}

export function clearProcessingHistory(): void {
  try { window.localStorage.removeItem(historyKey); } catch { /* local storage can be disabled */ }
}

export function recordWorkflowOpen(workflowId: string): WorkflowResumeItem {
  const item = { workflowId, openedAt: new Date().toISOString() };
  writeJSON(workflowResumeKey, item);
  return item;
}

export function loadWorkflowResume(): WorkflowResumeItem | undefined {
  const value = readJSON<unknown>(workflowResumeKey, undefined);
  if (!value || typeof value !== 'object') return undefined;
  const item = value as WorkflowResumeItem;
  return typeof item.workflowId === 'string' && typeof item.openedAt === 'string' ? item : undefined;
}

function readJSON<T>(key: string, fallback: T): T {
  try {
    const raw = window.localStorage.getItem(key);
    return raw === null ? fallback : JSON.parse(raw) as T;
  } catch { return fallback; }
}

function writeJSON(key: string, value: unknown): void {
  try { window.localStorage.setItem(key, JSON.stringify(value)); } catch { /* local storage can be disabled */ }
}
